import { createContext, useContext, useMemo } from 'react'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Permissions one part of the screen holds back from the user, whatever their role grants.
 *
 * Task assignment by team (Oct 2026): the task panel provides this when the viewer may read a
 * task but not work on it — a task assigned to someone else, or one still waiting in a team
 * pool. Inside it, `task:claim`, `task:cancel` and `task:escalate` read as not held, so every
 * action gate already written against these hooks (and `<RequirePermission>`, which reads
 * `useHasPermission`) hides itself without a change of its own.
 *
 * `null` — no provider, which is everywhere else — withholds nothing, and the hooks answer
 * exactly as they always have.
 */
export const WithheldPermissionsContext = createContext<ReadonlySet<string> | null>(null)

/**
 * The user's permissions minus the withheld ones. Kept separate from the hooks so the rule can
 * be checked without rendering. Returns the SAME array when nothing is withheld, so a caller
 * outside a provider sees no change at all, not even a new reference.
 */
export function withholdPermissions(
  permissions: string[] | undefined,
  withheld: ReadonlySet<string> | null,
): string[] | undefined {
  if (!permissions || !withheld || withheld.size === 0) return permissions
  return permissions.filter((p) => !withheld.has(p))
}

/** The permissions this part of the screen may use: the store's, less anything withheld here. */
function useEffectivePermissions(): string[] | undefined {
  const permissions = useAuthStore((s) => s.user?.permissions)
  const withheld = useContext(WithheldPermissionsContext)
  return useMemo(() => withholdPermissions(permissions, withheld), [permissions, withheld])
}

/**
 * Returns true if the current user holds [permission].
 *
 * Per RBAC Module spec #10 §3.1. Reads from `useAuthStore().user.permissions`
 * which is populated by the backend at login (`role.permissions ∪ user.permissions`).
 *
 * Mid-session updates: when an admin changes permissions, the FE polls
 * `/auth/me` on window focus (per spec #10 §5.5) to refresh the store.
 *
 * During mid-login / mid-logout, `user` is null → predicates return `false`.
 */
export function useHasPermission(permission: string): boolean {
  const permissions = useEffectivePermissions()
  return !!permissions?.includes(permission)
}

/**
 * Returns true if the current user holds ANY of the listed permissions.
 *
 * Useful for list-page header buttons where multiple actions might be
 * available, or "show this if user has any related permission" toggles.
 */
export function useHasAnyPermission(perms: string[]): boolean {
  const permissions = useEffectivePermissions()
  if (!permissions || perms.length === 0) return false
  return perms.some((p) => permissions.includes(p))
}

/**
 * Returns true if the current user holds ALL of the listed permissions.
 */
export function useHasAllPermissions(perms: string[]): boolean {
  const permissions = useEffectivePermissions()
  if (!permissions || perms.length === 0) return false
  return perms.every((p) => permissions.includes(p))
}

/**
 * Exposes the user's full permission list for display-only contexts
 * (e.g., UserDetailDrawer's "current permissions" pane).
 *
 * Withheld permissions are left out here too: the task footer's ActionButtons reads this list to
 * gate its buttons, so it must agree with the hooks above.
 */
export function useUserPermissions(): string[] {
  return useEffectivePermissions() ?? []
}
